import {
  AvailablePaymentCurrency,
  DashboardPaymentStatus,
  FormType,
  SubscriptionRecurrenceInterval,
} from "@simplyk/common";

import { DONOR_PAYMENT_CONTROLS, DonorPaymentControl } from "@/features/DonorDashboard/DonorPaymentDetailDrawer/types";

export interface AssistantPaymentCard {
  id: string;
  date: string;
  organizationName: string;
  amountInSmallestUnit: number;
  currency: AvailablePaymentCurrency;
  displayStatus: DashboardPaymentStatus;
  recurrenceInterval: SubscriptionRecurrenceInterval | null;
  formType: FormType;
  formId: string;
  commandId: string;
  subscriptionId: string | null;
  organizationId: string;
  formTitle: string | null;
  formCategory: string | null;
  formPath: string | null;
  itemCount: number | null;
}

const isOneOf = <T extends string>(values: Record<string, T>, value: unknown): value is T =>
  typeof value === "string" && (Object.values(values) as string[]).includes(value);

const isString = (value: unknown): value is string => typeof value === "string";

export type PaymentCardsPurpose = "choose" | "show";

export interface AssistantPaymentCards {
  purpose: PaymentCardsPurpose;
  payments: AssistantPaymentCard[];
  control: DonorPaymentControl | null;
}

const isPaymentControl = (value: unknown): value is DonorPaymentControl =>
  typeof value === "string" && (DONOR_PAYMENT_CONTROLS as readonly string[]).includes(value);

export const parsePaymentCards = (output: unknown): AssistantPaymentCards => {
  if (typeof output !== "object" || output === null || !("payments" in output) || !Array.isArray(output.payments)) {
    return { purpose: "show", payments: [], control: null };
  }
  const purpose = "purpose" in output && output.purpose === "choose" ? "choose" : "show";
  const control = "control" in output && isPaymentControl(output.control) ? output.control : null;
  return { purpose, payments: parsePayments(output.payments), control };
};

const parsePayments = (payments: Record<string, unknown>[]): AssistantPaymentCard[] =>
  payments.flatMap((payment: Record<string, unknown>): AssistantPaymentCard[] =>
    isString(payment.id) &&
    isString(payment.date) &&
    isString(payment.organizationName) &&
    typeof payment.amountInSmallestUnit === "number" &&
    isOneOf(AvailablePaymentCurrency, payment.currency) &&
    isOneOf(DashboardPaymentStatus, payment.displayStatus) &&
    isOneOf(FormType, payment.formType) &&
    isString(payment.formId) &&
    isString(payment.commandId) &&
    isString(payment.organizationId)
      ? [
          {
            id: payment.id,
            date: payment.date,
            organizationName: payment.organizationName,
            amountInSmallestUnit: payment.amountInSmallestUnit,
            currency: payment.currency,
            displayStatus: payment.displayStatus,
            recurrenceInterval: isOneOf(SubscriptionRecurrenceInterval, payment.recurrenceInterval)
              ? payment.recurrenceInterval
              : null,
            formType: payment.formType,
            formId: payment.formId,
            commandId: payment.commandId,
            subscriptionId: isString(payment.subscriptionId) ? payment.subscriptionId : null,
            organizationId: payment.organizationId,
            formTitle: isString(payment.formTitle) && payment.formTitle ? payment.formTitle : null,
            formCategory: isString(payment.formCategory) ? payment.formCategory : null,
            formPath: isString(payment.formPath) && payment.formPath.startsWith("/") ? payment.formPath : null,
            itemCount: typeof payment.itemCount === "number" ? payment.itemCount : null,
          },
        ]
      : []
  );
